// Copyright 2020 Sorama B.V.

import { PLATFORM } from "aurelia-pal";
import { GuidanceTopicType, IGuidanceTopic } from "catalogs/GuidanceCatalog";
import { WorkflowModuleType } from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import { WorkflowModes } from "services/WorkflowModes/WorkflowModes";

export class SpectralTimeAnalysisGuidanceCatalog {
    static SpectralTimeAnalysisOverviewModule: IGuidanceTopic = {
        name: "SpectralTimeAnalysis_Overview",
        modulePath: PLATFORM.moduleName("guidance/SpectralTimeAnalysis/Overview.html"),
        title: "Spectral & Time Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicType: GuidanceTopicType.TopicOverview,
        topicContent: "Spectral & Time Analysis Help",
    };

    static SpectralTimeAnalysisPowerOfBandModule: IGuidanceTopic = {
        name: "SpectralTimeAnalysis_PowerOfBand",
        modulePath: PLATFORM.moduleName("guidance/SpectralTimeAnalysis/HowCanIDeterminePowerOfABand.html"),
        title: "Spectral & Time Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How can I determine the power of a band?",
    };

    static SpectralTimeAnalysisGS1Module: IGuidanceTopic = {
        name: "SpectralTimeAnalysis_Q1", // https://sorama.atlassian.net/wiki/spaces/UI/pages/720175701/S1+When+should+I+use+the+streaming+section
        modulePath: PLATFORM.moduleName("guidance/SpectralTimeAnalysis/ST1WhatInTheFrequencySpectrum.html"),
        title: "Spectral & Time Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "What am I seeing in the Frequency Spectrum?",
        catchwords: [
            "frequency",
            "pressure",
            "weighting",
            "longer measurement = better resolution",
            "fourier transform",
            "average frequency spectrum",
        ],
    };
    static SpectralTimeAnalysisGS2Module: IGuidanceTopic = {
        name: "SpectralTimeAnalysis_Q2", // https://sorama.atlassian.net/wiki/spaces/UI/pages/720241030/ST2+What+am+I+seeing+in+a+Spectrogram+plot
        modulePath: PLATFORM.moduleName("guidance/SpectralTimeAnalysis/ST2WhatInTheSpectromPlot.html"),
        title: "Spectral & Time Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "What am I seeing in a Spectrogram plot?",
        catchwords: ["freq", "weighting", "time", "colormap"],
    };
    static SpectralTimeAnalysisGS3Module: IGuidanceTopic = {
        name: "SpectralTimeAnalysis_Q3", // https://sorama.atlassian.net/wiki/spaces/UI/pages/720241030/ST2+What+am+I+seeing+in+a+Spectrogram+plot
        modulePath: PLATFORM.moduleName("guidance/SpectralTimeAnalysis/ST3OctaveBandsAndWhyUsed.html"),
        title: "Spectral & Time Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "What are Octave Bands and why are they used?",
        catchwords: ["spectrum", "images"],
        hidden: true,
    };
    static SpectralTimeAnalysisGS4Module: IGuidanceTopic = {
        name: "SpectralTimeAnalysis_Q4", // https://sorama.atlassian.net/wiki/spaces/UI/pages/720240738/ST4+How+loud+is+my+sound+source+3+dB+rule
        modulePath: PLATFORM.moduleName("guidance/SpectralTimeAnalysis/ST4HowLoudIsSoundSource.html"),
        title: "Spectral & Time Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How loud is my sound source? 3 dB rule",
        catchwords: [""],
        hidden: true,
    };
    static SpectralTimeAnalysisGS6Module: IGuidanceTopic = {
        name: "SpectralTimeAnalysis_Q6", // https://sorama.atlassian.net/wiki/spaces/UI/pages/720175271/ST6+What+do+peaks+in+my+measurement+indicate
        modulePath: PLATFORM.moduleName("guidance/SpectralTimeAnalysis/ST6WhatDoPeaksInMyMeasurementIndicate.html"),
        title: "Spectral & Time Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "What do peaks in my measurement indicate?",
        catchwords: ["harmonics", "reference  background measurement", "octave bands"],
        hidden: true,
    };
    static SpectralTimeAnalysisGS7Module: IGuidanceTopic = {
        name: "SpectralTimeAnalysis_Q7", // https://sorama.atlassian.net/wiki/spaces/UI/pages/720175288/ST7+Is+there+a+link+between+the+peaks+in+the+spectrum
        modulePath: PLATFORM.moduleName("guidance/SpectralTimeAnalysis/ST7LinkBetweenPeaksInSpectrum.html"),
        title: "Spectral & Time Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "Is there a link between the peaks in the spectrum?",
        catchwords: ["harmonics", "reference background noise measurement", "rpm"],
        hidden: true,
    };
    static SpectralTimeAnalysisGS8Module: IGuidanceTopic = {
        name: "SpectralTimeAnalysis_Q8", // https://sorama.atlassian.net/wiki/spaces/UI/pages/720240816/ST8+Which+weighting+should+I+select+for+analysis
        modulePath: PLATFORM.moduleName("guidance/SpectralTimeAnalysis/ST8WhichWeightingShouldISelectForAnalysis.html"),
        title: "Spectral & Time Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "Which weighting should I select for analysis?",
        catchwords: ["A weighing", "B weighing", "C weighing", "Z weighing"],
    };
    static SpectralTimeAnalysisGS9Module: IGuidanceTopic = {
        name: "SpectralTimeAnalysis_Q9", // https://sorama.atlassian.net/wiki/spaces/UI/pages/719849054/ST9+How+do+I+know+if+my+object+produces+stationary+or+non-stationary+sound
        modulePath: PLATFORM.moduleName("guidance/SpectralTimeAnalysis/ST9StationaryOrNonAtationarySound.html"),
        title: "Spectral & Time Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How do I know if my object produces stationary or non-stationary sound?",
        catchwords: ["Spectrogram examples", "Time module visualization"],
    };
    static SpectralTimeAnalysisGS10Module: IGuidanceTopic = {
        name: "SpectralTimeAnalysis_Q10", // https://sorama.atlassian.net/wiki/spaces/UI/pages/823590929/ST10+What+am+I+seeing+in+the+Time+plot
        modulePath: PLATFORM.moduleName("guidance/SpectralTimeAnalysis/ST10WhatAmISeeingInTheTimePlot.html"),
        title: "Spectral & Time Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "What am I seeing in the Time plot?",
        catchwords: [""],
    };
    static SpectralTimeAnalysisColormapModule: IGuidanceTopic = {
        name: "SpectralTimeAnalysis_Q11",
        modulePath: PLATFORM.moduleName("guidance/SpectralTimeAnalysis/ST11WhichColormapShouldIChoose.html"),
        title: "Spectral & Time Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "Which colormap should I use?",
    };
    static SpectralTimeAnalysisGS12Module: IGuidanceTopic = {
        name: "SpectralTimeAnalysis_Q12",
        modulePath: PLATFORM.moduleName("guidance/SpectralTimeAnalysis/ST12WhatisRecommendedFrequency.html"),
        title: "Spectral & Time Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "What is Recommended Frequency?",
    };
    static SpectralTimeAnalysisGS13Module: IGuidanceTopic = {
        name: "SpectralTimeAnalysis_Q13",
        modulePath: PLATFORM.moduleName("guidance/SpectralTimeAnalysis/ST13WhatDoesCreateTimeRangeDo.html"),
        title: "Spectral & Time Analysis",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: WorkflowModes.All.filter((mode) => mode.canDoMeasurementAnalysis),
        topicParent: SpectralTimeAnalysisGuidanceCatalog.SpectralTimeAnalysisOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "What does Create Time Range do?",
    };
}
